import { SchoolUserExemptionReason } from './account.domain';

/**
 * Where a parent's request to deactivate their child's LOQT account has got to.
 *
 * This is the state of the request, never the pupil's own status: the pupil stays exactly as they
 * were until the school decides, and only an EXEMPTED decision touches the school-user record,
 * through the exemption mechanism that already exists.
 */
export type ChildDeactivationRequestStatus = 'PENDING' | 'APPROVED' | 'DECLINED' | 'EXEMPTED';

/**
 * What the parent asked for. An UNLINK only takes the pupil off the school's roster and leaves the
 * family's LOQT account active; both go through the same decision (DIGI-1734). Requests recorded
 * before unlinking needed approval have no type and are deletions: read them through
 * {@link requestTypeOf}.
 */
export type ChildDeactivationRequestType = 'DEACTIVATE' | 'UNLINK';

export function requestTypeOf(request: { requestType?: ChildDeactivationRequestType }): ChildDeactivationRequestType {
  return request?.requestType ?? 'DEACTIVATE';
}

/** One resolved round, kept so the school can see that this family asked before. */
export interface ChildDeactivationDecision {
  parentReason?: string;
  requestedDatetime?: string;
  status: ChildDeactivationRequestStatus;
  decidedByAccountId?: string;
  decidedByName?: string;
  decisionDatetime?: string;
  declineReason?: string;
  exemptionReason?: SchoolUserExemptionReason;
  exemptionNote?: string;
  requestType?: ChildDeactivationRequestType;
}

export interface ChildDeactivationRequest {
  id: string;
  childAccountId: string;
  childFirstName?: string;
  childLastName?: string;
  schoolAccountId: string;
  schoolUserId?: string;
  requestingParentAccountId?: string;
  requestingParentName?: string;
  /** Why the parent wants the account removed. The school decides from this. */
  parentReason: string;
  requestedDatetime?: string;
  status: ChildDeactivationRequestStatus;
  decidedByAccountId?: string;
  decidedByName?: string;
  decisionDatetime?: string;
  declineReason?: string;
  exemptionReason?: SchoolUserExemptionReason;
  exemptionNote?: string;
  previousDecisions?: ChildDeactivationDecision[];
  lastUpdatedDatetime?: string;
  requestType?: ChildDeactivationRequestType;
}

export const CHILD_DEACTIVATION_REQUEST_TYPE_LABELS: Record<ChildDeactivationRequestType, string> = {
  DEACTIVATE: 'Delete account',
  UNLINK: 'Unlink from school',
};

/** Labels shown in the UI. There is no i18n layer in this app, so these live with the type. */
export const CHILD_DEACTIVATION_STATUS_LABELS: Record<ChildDeactivationRequestStatus, string> = {
  PENDING: 'Awaiting your decision',
  APPROVED: 'Deactivation approved',
  DECLINED: 'Request declined',
  EXEMPTED: 'Pupil marked exempt',
};

/**
 * Bounds on the reason the school gives when declining. They mirror what the backend enforces, and
 * they are the same bounds the parent's own reason is held to, so both sides of the conversation
 * are treated alike.
 */
export const DECLINE_REASON_MIN_LENGTH = 10;
export const DECLINE_REASON_MAX_LENGTH = 500;
